/**
 * Cart-page ship-date revalidation.
 *
 * The ship promise (_quoted, _quickship and the customer-facing "Estimated Ship
 * Date") is stamped onto the line item when the cover is added to the cart, and
 * nothing re-checks it afterwards. A cart left overnight carries yesterday's
 * promise into checkout: order #1257 was placed at 15:50 on Aug 27 quoting
 * "Ships Aug 27, 2026", a same-day promise that had been generated the previous
 * afternoon and was no longer achievable once the noon cutoff had passed.
 *
 * This recomputes each cover line's promise from its variant's custom.ctpdate
 * (rendered by snippets/truck-fitment-cart-data.liquid, since /cart.js carries no
 * metafields) with the same rules as the product page -- PeragonFitment.shipPromise
 * in truck-fitment-data.js, which must load first. Where the answer has moved it
 * rewrites the line's properties and tells the shopper. One /cart.js call, fired
 * after render.
 *
 * Parts and accessories have no CTP date. With a cover in the cart they take the
 * cover's promise (the latest, if there are several); with none they are quoted by
 * the admin's lead-time tiers -- PeragonFitment.partPromise.
 *
 * Runs in two modes, set by data-mode on the script tag:
 *  - "cart" (cart page): always re-checks, reloads if a line changed, shows notice.
 *  - "site" (every other page): re-checks only when the noon cutoff has passed
 *    since the last check (localStorage), updates silently, and leaves the notice
 *    for the next cart page view.
 * Either way, clicking Checkout re-checks once more first (gateCheckout), so a line
 * added from the drawer since the page loaded is not sent to checkout unquoted.
 *
 * Deliberately cautious: any failure leaves the cart exactly as it was. It is far
 * better to carry a slightly stale date than to break someone's checkout.
 *
 * Note this does NOT close the gap on its own -- a shopper can still sit on the
 * checkout page past the cutoff, and an abandoned-cart link goes straight there.
 * The durable fix is for the order import (Acumatica) to recompute rather than
 * honouring the line item property. That is a customer-comms decision for Peragon,
 * pending.
 */
(function () {
  'use strict';

  const NOTICE_KEY = 'truck-fitment-cart-notice';
  const LAST_CHECK_KEY = 'truck-fitment-cart-checked';
  const CUTOFF_HOUR = 12;
  //Captured now: currentScript is null by the time a DOMContentLoaded handler runs.
  const SCRIPT = document.currentScript ||
                 document.querySelector('script[data-truck-fitment-cart]');

  // ---------------------------------------------------------------------------
  // Throttle (non-cart pages)
  // ---------------------------------------------------------------------------

  //Minutes Central time is ahead of UTC at `now` (negative), or null if the browser
  //cannot say.
  function centralOffset(now) {
    try {
      const parts = {};
      new Intl.DateTimeFormat('en-US', {
        timeZone: 'America/Chicago', hourCycle: 'h23',
        year: 'numeric', month: '2-digit', day: '2-digit',
        hour: '2-digit', minute: '2-digit', second: '2-digit'
      }).formatToParts(new Date(now)).forEach(function (p) { parts[p.type] = Number(p.value); });
      const wall = Date.UTC(parts.year, parts.month - 1, parts.day, parts.hour, parts.minute, parts.second);
      return Math.round((wall - Math.floor(now / 1000) * 1000) / 60000);
    } catch (err) {
      return null;
    }
  }

  /**
   * The quote only moves when the daily cutoff passes, so off the cart page we
   * re-ask only if a cutoff has passed since the last check. The cutoff is noon
   * CENTRAL, the same clock PeragonFitment.shipPromise uses -- not the shop's
   * timezone, which would let a check just before Central noon suppress the change
   * at noon. Falls back to the shop's UTC offset (data-shop-offset, "-0500"), then
   * the browser's own clock.
   */
  function lastCutoff(now, offsetAttr) {
    const m = /^([+-])(\d{2})(\d{2})$/.exec(offsetAttr || '');
    const central = centralOffset(now);
    const offsetMin = central !== null ? central : m
      ? (m[1] === '-' ? -1 : 1) * (parseInt(m[2], 10) * 60 + parseInt(m[3], 10))
      : -new Date(now).getTimezoneOffset();

    //Shift into shop-local "wall time", find today's noon there, shift back.
    const local = new Date(now + offsetMin * 60000);
    local.setUTCHours(CUTOFF_HOUR, 0, 0, 0);
    let cutoff = local.getTime() - offsetMin * 60000;
    if (cutoff > now) cutoff -= 24 * 3600 * 1000;
    return cutoff;
  }

  function readLastCheck() {
    try { return parseInt(localStorage.getItem(LAST_CHECK_KEY), 10) || 0; } catch (err) { return 0; }
  }

  function writeLastCheck(time) {
    try { localStorage.setItem(LAST_CHECK_KEY, String(time)); } catch (err) { /* storage unavailable */ }
  }

  // ---------------------------------------------------------------------------
  // Ship-date text
  // ---------------------------------------------------------------------------

  function dateOffset(days) {
    const d = new Date();
    if (days) d.setDate(d.getDate() + days);
    const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun',
                    'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
    return months[d.getMonth()] + ' ' + d.getDate();
  }

  /**
   * Turn the ship-date HTML into the same plain string the product page stores on
   * the line item.
   *
   * Kept in step with extractCleanShipDate() in truck-fitment.js -- that is the
   * source of truth for the format; this is its cart-side twin. The one difference
   * is the countdown: on a same-day quote the HTML holds an EMPTY
   * <span id="countdown"> that the product page fills from a ticking timer. Here we
   * fill it once from the same data-seconds and start no timer.
   */
  function cleanShipDate(html) {
    const temp = document.createElement('div');
    temp.innerHTML = html || '';

    const countdown = temp.querySelector('#countdown, [data-role="countdown"]');
    if (countdown) {
      const total = parseInt(countdown.getAttribute('data-seconds'), 10) || 0;
      const spent = parseInt(countdown.getAttribute('data-spent'), 10) || 0;
      const left = Math.max(0, total - spent);
      countdown.textContent = Math.floor(left / 3600) + 'h ' +
                              Math.floor((left % 3600) / 60) + 'm ' +
                              Math.floor(left % 60) + 's ';
    }

    //Remove the "Quickship:" label (icon + text) but not the countdown value --
    //both carry the .quickship class.
    temp.querySelectorAll('.quickship').forEach(function (el) {
      if (el !== countdown) el.remove();
    });

    let text = temp.textContent
      .replace(/^(quickship|quick ship)\s*:?\s*/i, '')
      .replace(/^ships\s*:\s*between\s*/i, 'Between ')
      .replace(/^ships\s*:\s*/i, '')
      .replace(/\s+/g, ' ')
      .trim();

    if (/if\s+ordered\s+within/i.test(text)) {
      text = text.replace(/\s+if\s+ordered\s+within.*$/i, '').trim();
    }
    if (/^(ships\s+)?today$/i.test(text)) return 'Ships TODAY - ' + dateOffset(0);
    if (/^(ships\s+)?tomorrow$/i.test(text)) return 'Ships TOMORROW - ' + dateOffset(1);
    return text;
  }

  // ---------------------------------------------------------------------------
  // Fetching
  // ---------------------------------------------------------------------------

  async function json(url, init) {
    const res = await fetch(url, init);
    if (!res.ok) throw new Error(url + ' -> HTTP ' + res.status);
    return res.json();
  }

  //{ <variant id>: { ctp, line } } for the cover lines in the cart at render time.
  function cartShipData() {
    const el = document.querySelector('script[data-truck-fitment-cart-data]');
    if (!el) return {};
    try { return JSON.parse(el.textContent) || {}; } catch (err) { return {}; }
  }

  // ---------------------------------------------------------------------------
  // Notice
  // ---------------------------------------------------------------------------

  function renderNotice(changes) {
    if (!changes || !changes.length) return;
    /*
    const anchor = document.querySelector('cart-items') ||
                   document.querySelector('.cart__items');
    if (!anchor || !anchor.parentNode) return;

    const box = document.createElement('div');
    box.className = 'truck-fitment-cart-notice';
    box.setAttribute('role', 'status');
    box.style.cssText = 'margin:0 0 16px;padding:12px 14px;border:1px solid #d8d8d3;' +
      'border-left:4px solid #1b1b1b;background:#f6f6f4;color:#1b1b1b;' +
      'font-size:14px;line-height:1.5;border-radius:3px;';

    const lead = document.createElement('strong');
    lead.textContent = changes.length > 1
      ? 'Shipping estimates updated'
      : 'Shipping estimate updated';
    lead.style.cssText = 'display:block;margin-bottom:4px;';
    box.appendChild(lead);

    changes.forEach(function (c) {
      const p = document.createElement('div');
      p.textContent = c.title + ' — now ' + c.to +
                      (c.from ? ' (previously ' + c.from + ')' : '');
      box.appendChild(p);
    });

    anchor.parentNode.insertBefore(box, anchor);
    */
  }

  // ---------------------------------------------------------------------------
  // Main
  // ---------------------------------------------------------------------------

  /**
   * Resolves true when every cover line was checked (so the throttle may be stamped),
   * false when one could not be and the next page view should retry.
   */
  async function revalidate(mode) {
    if (!window.PeragonFitment) {
      console.warn('[truck-fitment-cart] truck-fitment-data.js is not loaded');
      return false;
    }

    const cart = await json('/cart.js');
    //Gift cards and digital items do not ship: nothing to quote.
    const items = ((cart && cart.items) || []).filter(i => i.requires_shipping !== false);
    if (!items.length) return true;

    const isCover = i => !!(i.properties && i.properties._truck_id);
    const covers = items.filter(isCover);
    const parts = items.filter(i => !isCover(i));

    const shipData = cartShipData();
    let complete = true;
    const updates = [];

    //Queue a line whose promise has moved. change.js REPLACES the properties hash,
    //so send the whole thing back -- dropping the underscored keys here would strip
    //the fitment data off the order.
    function queue(item, quoted, quickship, text, visible) {
      const props = item.properties || {};
      if (String(props._quoted || '') === quoted) return;
      const properties = Object.assign({}, props);
      properties._quoted = quoted;
      properties._quickship = quickship ? '1' : '0';
      if (visible) {
        properties['Estimated Ship Date'] = text;
      } else {
        delete properties['Estimated Ship Date'];
      }
      updates.push({
        key: item.key,
        properties: properties,
        title: item.product_title || item.title || 'Your item',
        from: props['Estimated Ship Date'] || '',
        to: text
      });
    }

    //1. Covers: re-quote from the variant's ctpdate.
    const coverQuotes = [];
    covers.forEach(function (item) {
      const source = shipData[String(item.variant_id)];
      const fresh = source && window.PeragonFitment.shipPromise(source.ctp, source.line);
      if (!fresh) {
        complete = false;
        if (item.properties._quoted) {
          coverQuotes.push({
            quoted: String(item.properties._quoted),
            quickship: String(item.properties._quickship) === '1',
            text: item.properties['Estimated Ship Date'] || ''
          });
        }
        return;
      }
      const text = cleanShipDate(fresh.html);
      coverQuotes.push({ quoted: fresh.quoted, quickship: fresh.quickship, text: text });
      queue(item, fresh.quoted, fresh.quickship, text, true);
    });

    //2. Parts and accessories have no CTP date. With a cover in the cart they ship
    //with it (the latest cover date, if there are several); with none they take
    //the admin's lead-time tiers.
    if (parts.length) {
      let promise = null;
      if (coverQuotes.length) {
        promise = coverQuotes.reduce((a, b) => (b.quoted > a.quoted ? b : a));
      } else if (covers.length) {
        //A cover with no promise at all: nothing to copy. Leave parts alone.
        complete = false;
      } else {
        const p = window.PeragonFitment.partPromise(parts.map(i => i.title));
        promise = { quoted: p.quoted, quickship: p.quickship, text: cleanShipDate(p.html) };
      }
      if (promise) {
        parts.forEach(item => queue(item, promise.quoted, promise.quickship, promise.text, false));
      }
    }

    if (!updates.length) return complete;

    //Sequentially: cart mutations are not safe in parallel.
    const applied = [];
    let changed = 0;
    for (const u of updates) {
      try {
        await json('/cart/change.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
          body: JSON.stringify({ id: u.key, properties: u.properties })
        });
        changed++;
        if (!u.silent) applied.push({ title: u.title, from: u.from, to: u.to });
      } catch (err) {
        console.error('[truck-fitment-cart] could not update line ' + u.key, err);
      }
    }
    if (!changed) return false;

    //Checking out: the shopper is leaving, so no notice and no reload.
    if (mode === 'checkout') return complete;

    try {
      sessionStorage.setItem(NOTICE_KEY, JSON.stringify(applied));
    } catch (err) { /* private mode: no notice */ }

    //Off the cart page nothing on screen holds a line key, so the update stays
    //silent; the notice waits in sessionStorage for the next cart page view.
    if (mode !== 'cart') return complete;

    //Rewriting properties changes each line's key, and the theme's quantity and
    //remove controls hold the old one in data-index. Reload so the markup is
    //rebuilt against the new keys, carrying the notice across so the shopper is
    //told what moved rather than just seeing a different date.
    window.location.reload();
    return complete;
  }

  // ---------------------------------------------------------------------------
  // Checkout check
  // ---------------------------------------------------------------------------

  const GATE_TIMEOUT_MS = 4000;

  /**
   * Settle every line once more when Checkout is clicked. A part added from the
   * drawer since the page loaded has no promise yet. Gives up after
   * GATE_TIMEOUT_MS -- a missing date must never block a sale.
   */
  function gateCheckout() {
    document.addEventListener('submit', function (event) {
      const form = event.target;
      const button = event.submitter;
      if (!button || button.name !== 'checkout' || form.dataset.fitmentSettled) return;

      event.preventDefault();
      button.disabled = true;
      const timeout = new Promise(resolve => setTimeout(resolve, GATE_TIMEOUT_MS));
      Promise.race([revalidate('checkout'), timeout])
        .catch(err => console.warn('[truck-fitment-cart] checkout check skipped', err))
        .then(function () {
          form.dataset.fitmentSettled = '1';   //so the re-submit below passes straight through
          button.disabled = false;
          form.requestSubmit(button);
        });
    }, true);
  }

  function start() {
    const script = SCRIPT;
    const mode = script && script.dataset.mode === 'cart' ? 'cart' : 'site';
    const now = Date.now();

    gateCheckout();

    if (mode === 'cart') {
      let pending = null;
      try {
        pending = sessionStorage.getItem(NOTICE_KEY);
        if (pending) sessionStorage.removeItem(NOTICE_KEY);
      } catch (err) { /* storage unavailable */ }

      if (pending) {
        //This page view follows an update (the post-update reload, or a silent
        //update on another page). Show what changed and do not revalidate again,
        //so there is no way to loop.
        try { renderNotice(JSON.parse(pending)); } catch (err) { /* malformed */ }
        return;
      }
    } else if (readLastCheck() >= lastCutoff(now, script && script.dataset.shopOffset)) {
      //Already checked since the most recent cutoff: the quote cannot have moved.
      return;
    }

    revalidate(mode).then(function (complete) {
      if (complete) writeLastCheck(now);
    }).catch(function (err) {
      console.warn('[truck-fitment-cart] revalidation skipped', err);
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', start);
  } else {
    start();
  }
})();
